import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBackgroundBlur } from 'amazon-chime-sdk-component-library-react';
import MiniCheckBox from 'src/components/modules/MiniCheckBox/MiniCheckBox';
import DropDownMenu from 'src/components/base/DropDownMenu/DropDownMenu';
import {
  hideSelfView,
  setBlurBackground,
  setLocalVideoResolution,
  setVideoDevice,
  showSelfViews,
} from 'src/redux/actions/teleTherapyActions';
  import SelfVideoView from './SelfVideoView';
import './settings-modal.scss';
import { Resolutions } from './constant';
import { Typography } from "src/latest/src/components";

interface VideoSettingsProps {
  videoDevices: MediaDeviceInfo[];
}

const VideoSettings: React.FC<VideoSettingsProps> = ({
  videoDevices,
}) => {
  const dispatch = useDispatch();
  const { localVideoResolution, videoDevice, showSelfView, blurBackground } =
    useSelector(({ teleTherapy }: any) => teleTherapy);
  const [localResolution, setLocalResolution] = useState(localVideoResolution);
  const [selfView, setSelfView] = useState<boolean>(showSelfView);
  const { isBackgroundBlurSupported } =
    useBackgroundBlur();

  const toggleSelfView = (updatedSelfView: boolean) => {
    setSelfView(updatedSelfView);
    updatedSelfView ? dispatch(showSelfViews()) : dispatch(hideSelfView());
  };

  const updateBackgroundBlur = async (backgroundBlur: string) => {
    try {
      dispatch(setBlurBackground(backgroundBlur));
    } catch (error) {
      console.error('Error updating background blur:', error);
    }
  };

  const getDevicesAsOptions = (devices: MediaDeviceInfo[]) => {
    return devices?.map(device => ({
      value: device?.deviceId,
      title: device?.label,
    }));
  };

  const handleQualityChange = (quality: string) => {
    setLocalResolution(quality);
    dispatch(setLocalVideoResolution(quality));
  };

  const handleVideoDeviceSelection = async (deviceId: string) => {
    if (deviceId) {
      const selectedDevice = videoDevices.find(device => device.deviceId === deviceId);
      
      if(selectedDevice){
        dispatch(
          setVideoDevice(
            selectedDevice
          ),
        );
      }
    }
  };

  return (
    <div className="video-settings">
      <div className="upper-section">
        <div className="self-view-and-blur">
          <div className="self-view">
            <Typography
              variant="size-16"
              color="color-2"
              weight="semi-bold"
              className="mb-3"
            >
              Self view
            </Typography>
            <div className="self-view-checkbox">
              <MiniCheckBox
                text="Show self view"
                isActive={selfView}
                value={true}
                onClick={() => toggleSelfView(!selfView)}
                isCheckFirst={true}
              />
            </div>
          </div>
          <div className="background-blur">
            <Typography
              variant="size-16"
              color="color-2"
              weight="semi-bold"
              className="mb-3"
            >
              Background blur
            </Typography>
            <div className="blur-view-checkbox">
              <MiniCheckBox
                text="Disable"
                isActive={blurBackground === '' || blurBackground === false}
                value={true}
                onClick={() => updateBackgroundBlur('')}
                isCheckFirst={true}
                isRadio={true}
              />
              <MiniCheckBox
                text="Enable"
                isActive={blurBackground === 'light'}
                value={true}
                onClick={() =>
                  isBackgroundBlurSupported && updateBackgroundBlur('light')
                }
                isCheckFirst={true}
                isRadio={true}
              />
              {/* <MiniCheckBox
                      text="Heavy"
                      isActive={backgroundBlur === 'heavy'}
                      value={true}
                      onClick={() => isBackgroundBlurSupported && setBackgroundBlur('heavy')}
                      isCheckFirst={true}
                      isRadio={true}
                    /> */}
            </div>
            {!isBackgroundBlurSupported && (
              <Typography
                variant="size-12"
                color="color-2"
                weight="regular"
                className="mt-2"
              >
                Background blur is not supported on this device
              </Typography>
            )}
          </div>
        </div>
        <div className="video-device-and-resolution">
          <div className="video-device">
            <Typography
              variant="size-16"
              color="color-2"
              weight="semi-bold"
              className="mb-3"
            >
              Video device
            </Typography>
            <div className="video-device-dropdown pe-3">
              <DropDownMenu
                menu={getDevicesAsOptions(videoDevices)}
                toggler={videoDevice?.label}
                onClick={handleVideoDeviceSelection}
                className={'device-dropdown'}
              />
            </div>
          </div>
          <div className="video-device">
            <Typography
              variant="size-16"
              color="color-2"
              weight="semi-bold"
              className="mb-3"
            >
              Video Quality
            </Typography>
            <div className="video-resolution-dropdown ps-2">
              <DropDownMenu
                menu={Resolutions}
                toggler={localResolution}
                onClick={handleQualityChange}
                className={'device-dropdown'}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="lower-section pe-3">
        <SelfVideoView />
      </div>
    </div>
  );
};

export default VideoSettings;
